import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import { addToCart } from "../../../Features/Cart/cartSlice";
import { addToWishList, removeFromWishList } from "../../../Features/Wishlist/wishListSlice";
import { HiOutlineTruck, HiOutlineCalendar, HiOutlineReply } from "react-icons/hi";
import { HiOutlineCurrencyRupee } from "react-icons/hi";
import { RiScissorsLine, RiShoppingCartLine, RiRuler2Line, RiFileTextLine } from "react-icons/ri";
import { IoSparklesOutline, IoShareSocialOutline } from "react-icons/io5";
import toast from "react-hot-toast";
import placeholderFront from "../../../Assets/Products/product_1.jpg";
import placeholderBack from "../../../Assets/Products/product_1-1.jpg";
import { API_BASE, customerApi, getImageUrl } from "../../../api/client";
import { getRentalPriceRupees } from "../../../utils/rentalPricing";
import RelatedProducts from "../RelatedProducts/RelatedProducts";
import AskAISuggestionsModal from "./AskAISuggestionsModal";
import "./Product.css";
import Seo from "../../Seo/Seo";
import { useSiteSeo } from "../../../context/SiteSeoContext";
import { absoluteCanonicalUrl, getCanonicalSiteOrigin, toAbsoluteOgImage } from "../../../utils/siteUrl";
import { buildUniqueProductMeta, truncateMetaDescription } from "../../../utils/seoText";
import { usePrerenderReady } from "../../../utils/prerenderReady";
import { rememberRecentlyViewedProduct } from "../../../utils/recentlyViewed";

const TryOnModal = lazy(() => import("../TryOn/TryOnModal"));

/** Preset rental lengths shown in UI (admin product_prices may still define tiers for pricing). */
const RENT_PRESET_DAYS = [1, 3, 5, 7];

/** Inclusive calendar days between two YYYY-MM-DD strings (end >= start). */
function inclusiveDaysBetween(startStr, endStr) {
  if (!startStr || !endStr) return null;
  const d1 = new Date(`${startStr}T12:00:00`);
  const d2 = new Date(`${endStr}T12:00:00`);
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return null;
  if (d2 < d1) return null;
  return Math.round((d2 - d1) / 86400000) + 1;
}

function compareDateKeys(a, b) {
  if (!a || !b) return 0;
  return String(a).localeCompare(String(b));
}

/** Grid for rental range modal: pending first tap, or confirmed start/end (inclusive). */
function getRentalRangeGrid(year, month, pendingStart, rangeStart, rangeEnd, unavailableSet) {
  let lo = null;
  let hi = null;
  if (pendingStart && (!rangeStart || !rangeEnd)) {
    lo = hi = pendingStart;
  } else if (rangeStart && rangeEnd && compareDateKeys(rangeStart, rangeEnd) <= 0) {
    lo = rangeStart;
    hi = rangeEnd;
  }
  const rangeSet = new Set();
  if (lo && hi) {
    const d1 = new Date(`${lo}T12:00:00`);
    const d2 = new Date(`${hi}T12:00:00`);
    for (let t = d1.getTime(); t <= d2.getTime(); t += 86400000) {
      rangeSet.add(toDateKey(new Date(t)));
    }
  }
  const first = new Date(year, month, 1);
  const weekStart = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < weekStart; i++) cells.push({ dateKey: null, isCurrentMonth: false });
  for (let d = 1; d <= daysInMonth; d++) {
    const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const inRange = rangeSet.has(dateKey);
    const singleSpan = lo && hi && lo === hi;
    cells.push({
      dateKey,
      isCurrentMonth: true,
      isUnavailable: unavailableSet.has(dateKey) || isPastDateKey(dateKey),
      isRentalInRange: inRange,
      isRentalSingle: inRange && singleSpan,
      isRentalStart: inRange && !singleSpan && dateKey === lo,
      isRentalEnd: inRange && !singleSpan && dateKey === hi,
      isRentalMid: inRange && !singleSpan && dateKey !== lo && dateKey !== hi,
    });
  }
  const remainder = 42 - cells.length;
  for (let i = 0; i < remainder; i++) cells.push({ dateKey: null, isCurrentMonth: false });
  return cells;
}

function formatDisplayDate(date) {
  if (!date) return "";
  const d =
    typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)
      ? new Date(`${date}T12:00:00`)
      : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d.getDate()} ${months[d.getMonth()]}, ${d.getFullYear()}`;
}

function toDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const DELIVERY_DAYS_BEFORE_EVENT = 3;

/**
 * Preset rentals: eventDateStr = first day of possession; return = day after last rental day.
 * Custom rental: pass customRange { start, end } (YYYY-MM-DD inclusive); event in UI = last day (main event);
 * delivery is relative to rental start; return is the day after customRange.end.
 */
function getDeliveryReturnDates(eventDateStr, rentDays, customRange) {
  if (customRange?.start && customRange?.end) {
    const start = new Date(`${customRange.start}T12:00:00`);
    const end = new Date(`${customRange.end}T12:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return null;
    const delivery = new Date(start);
    delivery.setDate(delivery.getDate() - DELIVERY_DAYS_BEFORE_EVENT);
    const returnD = new Date(end);
    returnD.setDate(returnD.getDate() + 1);
    return { delivery, return: returnD, event: end, rangeStart: start };
  }
  if (!eventDateStr || rentDays < 1) return null;
  const event = new Date(`${eventDateStr}T12:00:00`);
  if (Number.isNaN(event.getTime())) return null;
  const delivery = new Date(event);
  delivery.setDate(delivery.getDate() - DELIVERY_DAYS_BEFORE_EVENT);
  const returnD = new Date(event);
  returnD.setDate(returnD.getDate() + rentDays);
  return { delivery, return: returnD, event };
}

function daysFromToday(dateKey) {
  if (!dateKey) return null;
  const d = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / 86400000);
}

/** True when dateKey (YYYY-MM-DD) is strictly before today in local time. */
function isPastDateKey(dateKey) {
  if (!dateKey) return false;
  const d = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  const t = new Date();
  d.setHours(0, 0, 0, 0);
  t.setHours(0, 0, 0, 0);
  return d < t;
}

function dateRangeHasBlocked(start, end, blockedDates) {
  if (!start || !end) return false;
  const d1 = new Date(`${start}T12:00:00`);
  const d2 = new Date(`${end}T12:00:00`);
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime()) || d2 < d1) return false;
  for (let t = d1.getTime(); t <= d2.getTime(); t += 86400000) {
    if (blockedDates.has(toDateKey(new Date(t)))) return true;
  }
  return false;
}

const MIN_DAYS_BEFORE_EVENT = 5;
const DELIVERY_DAYS_MIN = 3;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function getCalendarGrid(year, month, eventDateStr, rentDays, unavailableSet, customRange) {
  const first = new Date(year, month, 1);
  const weekStart = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  let rangeSet = new Set();
  let eventMarkerKey = eventDateStr;

  if (customRange?.start && customRange?.end) {
    const d1 = new Date(`${customRange.start}T12:00:00`);
    const d2 = new Date(`${customRange.end}T12:00:00`);
    for (let t = d1.getTime(); t <= d2.getTime(); t += 86400000) {
      rangeSet.add(toDateKey(new Date(t)));
    }
    eventMarkerKey = customRange.end;
  } else {
    const range = eventDateStr && rentDays >= 1
      ? getDeliveryReturnDates(eventDateStr, rentDays)
      : null;
    rangeSet = range
      ? (() => {
          const s = new Set();
          const start = range.event.getTime();
          const endInclusive = range.return.getTime() - 86400000;
          for (let t = start; t <= endInclusive; t += 86400000) s.add(toDateKey(new Date(t)));
          return s;
        })()
      : new Set();
  }

  const cells = [];
  for (let i = 0; i < weekStart; i++) cells.push({ dateKey: null, isCurrentMonth: false });
  for (let d = 1; d <= daysInMonth; d++) {
    const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({
      dateKey,
      isCurrentMonth: true,
      isUnavailable: unavailableSet.has(dateKey) || isPastDateKey(dateKey),
      isInRange: rangeSet.has(dateKey),
      isEventDate: eventMarkerKey === dateKey,
    });
  }
  const remainder = 42 - cells.length;
  for (let i = 0; i < remainder; i++) cells.push({ dateKey: null, isCurrentMonth: false });
  return cells;
}

const COLOR_NAME_TO_HEX = {
  blue: "#2563eb",
  red: "#dc2626",
  black: "#1f1f1f",
  white: "#f5f5f5",
  green: "#16a34a",
  yellow: "#eab308",
  pink: "#db2777",
  purple: "#7c3aed",
  orange: "#ea580c",
  navy: "#1e3a8a",
  grey: "#6b7280",
  gray: "#6b7280",
  beige: "#d4b896",
  brown: "#92400e",
  maroon: "#881337",
  gold: "#b45309",
  cream: "#fefce8",
  periwinkle: "#93c5fd",
};

function getHexForColorName(name) {
  if (!name) return "#6b7280";
  const key = String(name).trim().toLowerCase();
  return COLOR_NAME_TO_HEX[key] ?? "#6b7280";
}

function getContrastTextColor(hex) {
  const h = hex.replace(/^#/, "");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.4 ? "#1b1b1b" : "#ffffff";
}
const DEPOSIT_NOTE = "A refundable security deposit (50% of the rental amount) is charged at checkout and refunded after the outfit is returned in good condition.";

const Product = () => {
  const { id: productIdParam } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [blockedDates, setBlockedDates] = useState(new Set());
  const [availabilityLoadState, setAvailabilityLoadState] = useState("idle");
  const [currentImg, setCurrentImg] = useState(0);
  const [selectSize, setSelectSize] = useState("");
  const [selectedAttributes, setSelectedAttributes] = useState({});
  /** '1' | '3' | '5' | '7' | 'custom' */
  const [rentPreset, setRentPreset] = useState("1");
  const [customRangeStart, setCustomRangeStart] = useState("");
  const [customRangeEnd, setCustomRangeEnd] = useState("");
  const [rentalRangeModalOpen, setRentalRangeModalOpen] = useState(false);
  const [rentalRangeMonth, setRentalRangeMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [rentRangePickStart, setRentRangePickStart] = useState(null);
  const [eventDate, setEventDate] = useState("");
  const [zoomHover, setZoomHover] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: 0.5, y: 0.5 });
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [sizeChartModalOpen, setSizeChartModalOpen] = useState(false);
  const [tryOnModalOpen, setTryOnModalOpen] = useState(false);
  const [askAiModalOpen, setAskAiModalOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState({ average: 0, count: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } });
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", comment: "" });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const wishlistItems = useSelector((state) => state.wishlist.items);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const siteSeoCtx = useSiteSeo();
  const titleSuffix =
    siteSeoCtx.organization_name && String(siteSeoCtx.organization_name).trim()
      ? ` | ${String(siteSeoCtx.organization_name).trim()}`
      : " | Gem Fashion Studios";

  usePrerenderReady(!loading && (Boolean(product) || notFound));

  const productId = productIdParam ? parseInt(productIdParam, 10) : null;
  const PLACEHOLDER_IMAGES = [placeholderFront, placeholderBack];
  const rawImages = product?.images?.length ? product.images.map((i) => (typeof i === "string" ? i : i.url)) : [];
  const productImages = rawImages.length > 0 ? rawImages : PLACEHOLDER_IMAGES;
  const resolveImageSrc = (src) => {
    if (!src) return PLACEHOLDER_IMAGES[0];
    if (src === "/placeholder-front.jpg" || src === "/placeholder-front.svg") return placeholderFront;
    if (src === "/placeholder-back.jpg" || src === "/placeholder-back.svg") return placeholderBack;
    return getImageUrl(src) || src;
  };
  const productPrices = product?.prices || [];
  const rentDays =
    rentPreset === "custom"
      ? (() => {
          if (customRangeStart && customRangeEnd) {
            const n = inclusiveDaysBetween(customRangeStart, customRangeEnd);
            return n != null && n >= 1 ? Math.min(90, n) : 8;
          }
          return 8;
        })()
      : Number.parseInt(rentPreset, 10) || 1;
  const rentalPriceRupees = getRentalPriceRupees(productPrices, rentDays);
  const deliveryReturn =
    rentPreset === "custom" && customRangeStart && customRangeEnd
      ? getDeliveryReturnDates(null, rentDays, { start: customRangeStart, end: customRangeEnd })
      : getDeliveryReturnDates(eventDate, rentDays);
  const availabilityRangeStart = rentPreset === "custom" ? customRangeStart : eventDate;
  const availabilityRangeEnd = rentPreset === "custom" ? customRangeEnd : (deliveryReturn ? toDateKey(new Date(deliveryReturn.return.getTime() - 86400000)) : "");
  const availabilityStatus = !availabilityRangeStart || !availabilityRangeEnd
    ? "Select rental date to check availability"
    : availabilityLoadState === "loading"
      ? "Checking availability…"
      : availabilityLoadState === "error"
        ? "Unable to check availability"
    : dateRangeHasBlocked(availabilityRangeStart, availabilityRangeEnd, blockedDates)
      ? "Not available"
      : "Available for your dates";
  const productCode = product?.code || "";
  const isWishlisted = wishlistItems.some((item) => String(item.productId ?? item.productID ?? item.id) === String(productId));
  const productName = product?.name || "";
  const productColor = product?.color || "";
  const attributes = product?.attributes && typeof product.attributes === "object" && Object.keys(product.attributes).length > 0
    ? product.attributes
    : null;
  const sizes = attributes?.Size ?? (Array.isArray(product?.sizes) && product.sizes.length ? product.sizes : (product?.sizes ? [product.sizes] : []));
  /** One fabric value → show in specs only; multiple → picker below (avoids duplicate "MATERIAL" block). */
  const materialMulti = Array.isArray(attributes?.Material) && attributes.Material.length > 1;
  const materialLineRaw = !materialMulti
    ? (product?.material && String(product.material).trim()) ||
      (attributes?.Material?.length === 1 ? String(attributes.Material[0]).trim() : "")
    : "";
  const materialLine = materialLineRaw || null;

  const sliderIntervalRef = useRef(null);

  useEffect(() => {
    if (!productIdParam) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    setLoading(true);
    setNotFound(false);
    const base = API_BASE || "";
    fetch(`${base}/api/products/${productIdParam}`)
      .then((r) => {
        if (!r.ok) throw new Error("Not found");
        return r.json();
      })
      .then((data) => {
        setProduct(data);
        setRentPreset("1");
        setCustomRangeStart("");
        setCustomRangeEnd("");
        setRentRangePickStart(null);
        if (data.sizes?.length) setSelectSize(data.sizes[0]);
        const attrs = data.attributes && typeof data.attributes === "object" ? data.attributes : null;
        if (attrs) {
          const initial = {};
          Object.keys(attrs).forEach((k) => {
            const v = attrs[k];
            if (Array.isArray(v) && v.length) initial[k] = v[0];
          });
          setSelectedAttributes(initial);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [productIdParam]);

  useEffect(() => {
    if (!productIdParam || !product) return;
    const today = new Date();
    const from = toDateKey(today);
    const d = new Date(today);
    d.setMonth(d.getMonth() + 3);
    const to = toDateKey(d);
    setAvailabilityLoadState("loading");
    fetch(`${API_BASE}/api/products/${productIdParam}/availability?from=${from}&to=${to}`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('availability fetch failed')))
      .then((data) => {
        setBlockedDates(new Set(data.blocked_dates || []));
        setAvailabilityLoadState("ready");
      })
      .catch(() => setAvailabilityLoadState("error"));
  }, [productIdParam, product]);

  const loadReviews = () => {
    if (!productIdParam) return;
    fetch(`${API_BASE || ""}/api/products/${productIdParam}/reviews`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("reviews fetch failed"))))
      .then((data) => {
        setReviews(Array.isArray(data.reviews) ? data.reviews : []);
        setReviewSummary(data.summary || { average: 0, count: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } });
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadReviews();
  }, [productIdParam]);

  const submitReview = async (e) => {
    e.preventDefault();
    setReviewSubmitting(true);
    try {
      await customerApi(`/api/products/${productIdParam}/reviews`, {
        method: "POST",
        body: JSON.stringify(reviewForm),
      });
      toast.success("Review submitted for moderation");
      setReviewForm({ rating: 5, title: "", comment: "" });
      loadReviews();
    } catch (err) {
      toast.error(err.message || "Please sign in to submit a review");
    } finally {
      setReviewSubmitting(false);
    }
  };

  const startSlider = () => {
    if (productImages.length <= 1 || sliderIntervalRef.current) return;
    sliderIntervalRef.current = setInterval(() => {
      setCurrentImg((i) => (i + 1) % productImages.length);
    }, 3500);
  };

  const stopSlider = () => {
    if (sliderIntervalRef.current) {
      clearInterval(sliderIntervalRef.current);
      sliderIntervalRef.current = null;
    }
  };

  useEffect(() => {
    startSlider();
    return () => stopSlider();
  }, [productImages.length]);

  useEffect(() => {
    if (!attributes?.Material || attributes.Material.length !== 1) return;
    setSelectedAttributes((prev) => {
      if (prev.Material) return prev;
      return { ...prev, Material: attributes.Material[0] };
    });
  }, [product?.id, attributes?.Material]);

  useEffect(() => {
    if (calendarOpen && eventDate) {
      const d = new Date(`${eventDate}T12:00:00`);
      setCalendarMonth({ year: d.getFullYear(), month: d.getMonth() });
    } else if (calendarOpen && !eventDate) {
      const d = new Date();
      setCalendarMonth({ year: d.getFullYear(), month: d.getMonth() });
    }
  }, [calendarOpen, eventDate]);

  /** Custom rental: main event day = last day of the range (e.g. the 18th); drives event field + return math. */
  useEffect(() => {
    if (rentPreset !== "custom" || !customRangeStart || !customRangeEnd) return;
    setEventDate(customRangeEnd);
  }, [rentPreset, customRangeStart, customRangeEnd]);

  const calendarGrid = getCalendarGrid(
    calendarMonth.year,
    calendarMonth.month,
    eventDate,
    rentDays,
    blockedDates,
    rentPreset === "custom" && customRangeStart && customRangeEnd
      ? { start: customRangeStart, end: customRangeEnd }
      : null
  );

  const rentalRangeGrid = getRentalRangeGrid(
    rentalRangeMonth.year,
    rentalRangeMonth.month,
    rentRangePickStart,
    customRangeStart,
    customRangeEnd,
    blockedDates
  );

  const openRentalRangeModal = () => {
    setRentRangePickStart(null);
    const anchor = customRangeStart || customRangeEnd;
    if (anchor) {
      const d = new Date(anchor);
      setRentalRangeMonth({ year: d.getFullYear(), month: d.getMonth() });
    } else {
      const d = new Date();
      setRentalRangeMonth({ year: d.getFullYear(), month: d.getMonth() });
    }
    setRentalRangeModalOpen(true);
  };

  const handleRentalRangeCellClick = (dateKey) => {
    if (!dateKey || blockedDates.has(dateKey) || isPastDateKey(dateKey)) return;
    if (!rentRangePickStart) {
      setRentRangePickStart(dateKey);
      setCustomRangeStart("");
      setCustomRangeEnd("");
      return;
    }
    let start = rentRangePickStart;
    let end = dateKey;
    if (compareDateKeys(end, start) < 0) [start, end] = [end, start];
    setCustomRangeStart(start);
    setCustomRangeEnd(end);
    setRentRangePickStart(null);
    setRentalRangeModalOpen(false);
  };

  const rentalRangePrevMonth = () => {
    setRentalRangeMonth((prev) => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 };
      return { year: prev.year, month: prev.month - 1 };
    });
  };
  const rentalRangeNextMonth = () => {
    setRentalRangeMonth((prev) => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 };
      return { year: prev.year, month: prev.month + 1 };
    });
  };
  const rentalRangePrevYear = () => setRentalRangeMonth((prev) => ({ ...prev, year: prev.year - 1 }));
  const rentalRangeNextYear = () => setRentalRangeMonth((prev) => ({ ...prev, year: prev.year + 1 }));

  const closeRentalRangeModal = () => {
    setRentalRangeModalOpen(false);
    setRentRangePickStart(null);
  };

  const handleCalendarDateSelect = (dateKey) => {
    if (!dateKey || blockedDates.has(dateKey) || isPastDateKey(dateKey)) return;
    if (rentPreset === "custom" && customRangeStart && customRangeEnd) {
      return;
    }
    const days = daysFromToday(dateKey);
    if (days !== null && days < MIN_DAYS_BEFORE_EVENT) {
      toast("Book 5–7 days before your event for reliable delivery.", {
        icon: "⚠️",
        style: { background: "#fef3c7", color: "#92400e", border: "1px solid #f59e0b" },
      });
    }
    setEventDate(dateKey);
  };

  const calendarPrevMonth = () => {
    setCalendarMonth((prev) => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 };
      return { year: prev.year, month: prev.month - 1 };
    });
  };
  const calendarNextMonth = () => {
    setCalendarMonth((prev) => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 };
      return { year: prev.year, month: prev.month + 1 };
    });
  };
  const calendarPrevYear = () => setCalendarMonth((prev) => ({ ...prev, year: prev.year - 1 }));
  const calendarNextYear = () => setCalendarMonth((prev) => ({ ...prev, year: prev.year + 1 }));

  const handleZoomMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    setCursorPos({ x, y });
  };

  useEffect(() => {
    if (!product) return;
    const next = rememberRecentlyViewedProduct({
      id: product.id,
      name: product.name,
      image: productImages[0] || "",
      price: rentalPriceRupees,
    });
    setRecentlyViewed(next.filter((item) => String(item.id) !== String(product.id)).slice(0, 4));
  }, [product?.id]);

  const toggleWishlist = () => {
    if (!product || !productId) return;
    const item = {
      productId,
      productID: productId,
      productName: product.name,
      name: product.name,
      productPrice: rentalPriceRupees,
      frontImg: productImages[0] || "",
    };
    if (isWishlisted) {
      dispatch(removeFromWishList(item));
      toast.success("Removed from wishlist");
    } else {
      dispatch(addToWishList(item));
      toast.success("Added to wishlist");
    }
  };

  const handleRentNow = () => {
    if (!product || !productId) return;
    if (rentPreset === "custom" && (!customRangeStart || !customRangeEnd)) {
      toast.error("Please choose your rental period (Custom → pick start and end on the calendar).");
      return;
    }
    if (!eventDate) {
      toast.error("Please select your event date.");
      return;
    }
    const attrs = attributes ? selectedAttributes : null;
    const productDetails = {
      productId,
      productID: productId,
      productName: product.name,
      productPrice: rentalPriceRupees,
      frontImg: productImages[0] || "",
      rent_days: rentDays,
      event_date: eventDate,
      size: selectSize || null,
      selectedAttributes: attrs && Object.keys(attrs).length ? attrs : null,
    };
    const inCart = cartItems.find(
      (item) => (item.productId ?? item.productID) === productId && (item.event_date ?? "") === eventDate && (item.rent_days ?? 1) === rentDays &&
        (() => {
          const a = item.selectedAttributes;
          const b = attrs;
          if (!a && !b) return true;
          if (!a || !b) return false;
          const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
          for (const k of keys) if (String(a[k] ?? '') !== String(b[k] ?? '')) return false;
          return true;
        })()
    );
    if (inCart && inCart.quantity >= 20) {
      toast.error("Rental limit reached");
    } else {
      dispatch(addToCart(productDetails));
      toast.success("Added to rentals!");
    }
  };

  if (loading) {
    const safePid =
      productIdParam != null && String(productIdParam).trim()
        ? String(productIdParam).trim().replace(/[^\w.-]/g, "")
        : "";
    const canonPath = safePid ? `/product/${safePid}` : "/shop";
    return (
      <>
        <Seo
          title={`Designer rental outfit${titleSuffix}`}
          description={truncateMetaDescription(
            "Rent designer clothing and outfits. View sizes, rental pricing, delivery timing, and availability on Gem Fashion Studios."
          )}
          canonicalUrl={absoluteCanonicalUrl(canonPath)}
        />
        <div className="productSection productSectionRental" style={{ padding: "2rem", textAlign: "center" }}>
          Loading product…
        </div>
      </>
    );
  }
  if (notFound || !product) {
    const safePid =
      productIdParam != null && String(productIdParam).trim()
        ? String(productIdParam).trim().replace(/[^\w.-]/g, "")
        : "";
    const canonPath = safePid ? `/product/${safePid}` : "/shop";
    return (
      <>
        <Seo
          title={`Product not found${titleSuffix}`}
          description="This outfit may have been moved or is no longer available for rent."
          canonicalUrl={absoluteCanonicalUrl(canonPath)}
          noindex
        />
        <div className="productSection productSectionRental" style={{ padding: "2rem", textAlign: "center" }}>
          <p>Product not found.</p>
          <Link to="/shop">Back to shop</Link>
        </div>
      </>
    );
  }

  const origin = getCanonicalSiteOrigin();
  const canonicalProductUrl = absoluteCanonicalUrl(`/product/${product.id}`);
  const metaTitleRaw = (product.seo_title || product.meta_title || product.name || "Designer rental outfit").trim();
  const descSource = product.seo_description || product.short_description || product.description || product.name || "";
  const { title: metaTitle, description: metaDesc } = buildUniqueProductMeta({
    id: product.id,
    title: metaTitleRaw,
    description: descSource,
    titleSuffix,
  });
  const keyphrase =
    product.meta_keyphrase != null && String(product.meta_keyphrase).trim()
      ? String(product.meta_keyphrase).trim()
      : "";

  const firstRaw = rawImages.length ? rawImages[0] : "";
  const ogResolved = product.og_image_url
    ? toAbsoluteOgImage(product.og_image_url, getImageUrl)
    : firstRaw ? toAbsoluteOgImage(firstRaw, getImageUrl) : "";
  const imageAltBase = `${productName}${productColor ? ` – ${productColor}` : ""}`;

  const paiseList = (productPrices || [])
    .map((p) => p.amount_paise)
    .filter((x) => x != null && Number.isFinite(Number(x)));
  const minPaise = paiseList.length ? Math.min(...paiseList.map(Number)) : null;
  const minRupee = minPaise != null ? (minPaise / 100).toFixed(2) : undefined;

  const ldImages = rawImages
    .slice(0, 8)
    .map((u) => (u ? toAbsoluteOgImage(u, getImageUrl) : ""))
    .filter(Boolean);

  const brandName = siteSeoCtx.organization_name || "Gem Fashion Studios";

  const breadcrumbLd = origin
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "Shop", item: `${origin}/shop` },
          ...(product.category?.name
            ? [
                {
                  "@type": "ListItem",
                  position: 3,
                  name: String(product.category.name),
                  item: `${origin}/shop`,
                },
              ]
            : []),
          {
            "@type": "ListItem",
            position: product.category?.name ? 4 : 3,
            name: metaTitleRaw,
            item: canonicalProductUrl,
          },
        ],
      }
    : null;

  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: metaTitleRaw,
    sku: product.code || undefined,
    image: ldImages.length ? ldImages : undefined,
    description: metaDesc,
    ...(product.category?.name ? { category: product.category.name } : {}),
    brand: {
      "@type": "Brand",
      name: brandName,
    },
    ...(minRupee
      ? {
          offers: {
            "@type": "Offer",
            url: canonicalProductUrl,
            priceCurrency: "INR",
            price: minRupee,
            priceValidUntil: new Date(Date.now() + 90 * 86400000).toISOString().split("T")[0],
            availability: "https://schema.org/InStock",
            itemCondition: "https://schema.org/NewCondition",
            seller: { "@type": "Organization", name: brandName },
          },
        }
      : {}),
    ...(reviewSummary.count > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: String(reviewSummary.average),
            reviewCount: reviewSummary.count,
          },
          review: reviews.slice(0, 10).map((r) => ({
            "@type": "Review",
            reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
            author: { "@type": "Person", name: r.display_name || "Gem Fashion customer" },
            datePublished: r.created_at ? String(r.created_at).slice(0, 10) : undefined,
            name: r.title || undefined,
            reviewBody: r.comment,
          })),
        }
      : {}),
  };

  const jsonLdForPage = [productLd, ...(breadcrumbLd ? [breadcrumbLd] : [])];

  return (
    <>
      <Seo
        title={metaTitle}
        description={metaDesc}
        canonicalUrl={canonicalProductUrl}
        ogImage={ogResolved || undefined}
        ogImageAlt={imageAltBase}
        ogType="product"
        keywords={keyphrase || undefined}
        jsonLd={jsonLdForPage}
      />
      <div className="productSection productSectionRental">
      <div className="productShowCase">
        <div className="productGallery">
          <div className="productGalleryCol">
            <div className="productGalleryRow">
              <div className="productThumb">
                {(productImages.length ? productImages : [""]).map((img, i) => (
                  <img
                    key={i}
                    src={resolveImageSrc(img) || PLACEHOLDER_IMAGES[0]}
                    alt={productImages.length > 1 ? `${imageAltBase}, view ${i + 1}` : imageAltBase}
                    className={currentImg === i ? "active" : ""}
                    onClick={() => setCurrentImg(i)}
                  />
                ))}
              </div>
              <div className="productGalleryRight">
                <div
                  className="productFullImg"
                  onMouseEnter={() => { stopSlider(); setZoomHover(true); }}
                  onMouseLeave={() => { startSlider(); setZoomHover(false); }}
                  onMouseMove={handleZoomMouseMove}
                >
                  <img
                    fetchPriority="high"
                    src={resolveImageSrc(productImages[currentImg] || productImages[0]) || PLACEHOLDER_IMAGES[0]}
                    alt={imageAltBase}
                    style={
                      zoomHover
                        ? {
                            transform: `scale(2) translate(${(0.5 - cursorPos.x) * 100}%, ${(0.5 - cursorPos.y) * 100}%)`,
                          }
                        : undefined
                    }
                  />
                </div>
                <div className="productGalleryTryOn">
                  <button type="button" className="productGalleryTryOnBtn productGalleryTryOnBtn--try" onClick={() => setTryOnModalOpen(true)}>
                    <RiScissorsLine size={20} />
                    <span>Try This Dress</span>
                  </button>
                  <button type="button" className="productGalleryTryOnBtn productGalleryTryOnBtn--ai" onClick={() => setAskAiModalOpen(true)}>
                    <IoSparklesOutline size={20} />
                    <span>Ask AI</span>
                  </button>
                  <button type="button" className="productGalleryTryOnBtn productGalleryTryOnBtn--share productGalleryTryOnBtn--iconOnly" aria-label="Share this dress" onClick={() => { const url = window.location.href; if (navigator.share) { navigator.share({ title: productName || "Dress", url }).catch(() => {}); } else { navigator.clipboard?.writeText(url).then(() => toast.success("Link copied")).catch(() => {}); } }}>
                    <IoShareSocialOutline size={22} />
                  </button>
                </div>
                <div className="productGalleryDelivery">
                  <div className="productGalleryDeliveryIcon">
                    <HiOutlineTruck size={20} />
                  </div>
                  <div className="productGalleryDeliveryText">
                    <p><strong>Estimate delivery: 3–5 days</strong></p>
                    <p>Book 5–7 days before your event.</p>
                    <p>Return within 3 days. Duties & taxes non-refundable.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Video — shown only when video_url is set */}
        {product.video_url && (
          <div className="productVideoSection">
            <h3 className="productVideoTitle">Watch It On</h3>
            {product.video_url.includes('youtube') || product.video_url.includes('youtu.be') ? (
              <div className="productVideoEmbed">
                <iframe
                  src={product.video_url}
                  title={`${productName} video`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="productVideoEmbed">
                <video controls preload="metadata">
                  <source src={product.video_url} />
                  Your browser does not support the video tag.
                </video>
              </div>
            )}
          </div>
        )}

        <div className="productDetails productDetailsRental">
          <div className="productBreadcrumb">
            <div className="breadcrumbLink">
              <Link to="/">Home</Link>&nbsp;/&nbsp;
              <Link to="/shop">Browse Rentals</Link>
            </div>
          </div>

          <h1 className="productNameRental">{productName}</h1>
          <p className="productDescRental">
            {product?.description || "Rental outfit – see details above."}
          </p>

          <div className="productPriceRentalWrap">
            <div className="productPriceRental">
              <span className="priceAmount">₹ {rentalPriceRupees.toLocaleString("en-IN")}.00</span>
              <span className="priceLabel">Rent</span>
            </div>
            <p className="priceTaxNote">Inclusive all taxes</p>
          </div>

          <div className="productServiceOptions">
            <button type="button" className="serviceOptionBtn serviceOptionBtn--fitting" onClick={() => setTermsModalOpen(true)}>
              <RiFileTextLine size={28} />
              <span>Terms & Conditions</span>
            </button>
            <button type="button" className="serviceOptionBtn serviceOptionBtn--deposit" onClick={() => setDepositModalOpen(true)}>
              <HiOutlineCurrencyRupee size={28} />
              <span>Refundable Deposit</span>
            </button>
            <button type="button" className="serviceOptionBtn serviceOptionBtn--delivery" onClick={() => setDeliveryModalOpen(true)}>
              <HiOutlineTruck size={28} />
              <span>Free pick up/Delivery</span>
            </button>
          </div>

          <div className="productSpecs">
            <p><strong>PRODUCT CODE:</strong> {productCode}</p>
            {product?.gender && (
              <p><strong>FOR:</strong> {product.gender}</p>
            )}
            <p className="productSpecColor">
              <strong>COLOR:</strong>{" "}
              <span
                className="productColorBadge"
                style={{
                  backgroundColor: getHexForColorName(productColor),
                  color: getContrastTextColor(getHexForColorName(productColor)),
                }}
              >
                {productColor}
              </span>
            </p>
            {materialLine ? (
              <p>
                <strong>MATERIAL:</strong> {materialLine}
              </p>
            ) : null}
          </div>

          {attributes ? (
            <>
              {Object.entries(attributes)
                .filter(([attrName, values]) => {
                  const k = attrName.toLowerCase();
                  if (k === "color") return false;
                  if (k === "material") {
                    if (!Array.isArray(values) || values.length === 0) return false;
                    return values.length > 1;
                  }
                  return true;
                })
                .map(([attrName, values]) => {
                  if (!Array.isArray(values) || values.length === 0) return null;
                  const isSizeAttr = ["size", "sizes"].includes(attrName.toLowerCase());
                  return (
                    <div key={attrName} className="productSizeRental" style={{ marginBottom: "1rem" }}>
                      <span
                        className="label"
                        style={{
                          display: "block",
                          flexBasis: "100%",
                          width: "100%",
                          marginBottom: "0.5rem",
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {attrName}
                      </span>
                      <div className="sizeOptionsRental">
                        {values.map((val) => (
                          <button
                            key={val}
                            type="button"
                            className={`sizeOptionRental ${selectedAttributes[attrName] === val ? "selected" : ""}`}
                            onClick={() => setSelectedAttributes((prev) => ({ ...prev, [attrName]: val }))}
                          >
                            {val}
                          </button>
                        ))}
                      </div>
                      {isSizeAttr ? (
                        <>
                          <span className="sizeChartDot" aria-hidden="true">
                            ·
                          </span>
                          <span className="sizeChartSpacer" aria-hidden="true" />
                          <button type="button" className="sizeChartLink" onClick={() => setSizeChartModalOpen(true)}>
                            <RiRuler2Line size={18} />
                            Size Chart
                          </button>
                        </>
                      ) : null}
                    </div>
                  );
                })}
            </>
          ) : (
            <div className="productSizeRental">
              <div className="sizeOptionsRental">
                {sizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    className={`sizeOptionRental ${selectSize === size ? "selected" : ""}`}
                    onClick={() => setSelectSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
              <span className="sizeChartDot" aria-hidden="true">
                ·
              </span>
              <span className="sizeChartSpacer" aria-hidden="true" />
              <button type="button" className="sizeChartLink" onClick={() => setSizeChartModalOpen(true)}>
                <RiRuler2Line size={18} />
                Size Chart
              </button>
            </div>
          )}

          <div className="productRentPeriod">
            <span className="label">RENT PERIOD</span>
            <div className="rentPeriodOptions">
              {RENT_PRESET_DAYS.map((d) => (
                <label key={d} className="rentPeriodLabel">
                  <input
                    type="radio"
                    name="rentPreset"
                    value={String(d)}
                    checked={rentPreset === String(d)}
                    onChange={() => setRentPreset(String(d))}
                  />
                  <span>{d === 1 ? "1 day" : `${d} days`}</span>
                </label>
              ))}
              <label className="rentPeriodLabel">
                <input
                  type="radio"
                  name="rentPreset"
                  value="custom"
                  checked={rentPreset === "custom"}
                  onChange={() => setRentPreset("custom")}
                />
                <span>Custom</span>
              </label>
            </div>
            {rentPreset === "custom" ? (
              <div className="rentPeriodCustom">
                <p className="rentPeriodCustomSummary">
                  {customRangeStart && customRangeEnd ? (
                    <>
                      <span className="rentPeriodCustomDatesText">
                        {formatDisplayDate(customRangeStart)} → {formatDisplayDate(customRangeEnd)}
                      </span>
                      <span className="rentPeriodCustomDaysBadge">
                        {inclusiveDaysBetween(customRangeStart, customRangeEnd) ?? "—"} days (inclusive)
                      </span>
                    </>
                  ) : (
                    "Tap the calendar twice: first day = start, second = end (inclusive)."
                  )}
                </p>
                <button type="button" className="rentPeriodCustomChooseBtn" onClick={openRentalRangeModal}>
                  {customRangeStart && customRangeEnd ? "Change rental dates" : "Choose rental dates"}
                </button>
                <p className="rentPeriodCustomHint">Price follows your product&apos;s rental tiers; we interpolate if needed.</p>
              </div>
            ) : null}
          </div>

          <div className="productEventDate">
            <p className={`productAvailabilityStatus ${availabilityStatus.startsWith("Available") ? "available" : availabilityStatus.startsWith("Not") ? "notAvailable" : "pending"}`}>
              {availabilityStatus}
            </p>
            {rentPreset !== "custom" ? <span className="label">EVENT DATE</span> : null}
            <div
              className={`productEventDateRow${rentPreset === "custom" ? " productEventDateRow--customOnly" : ""}`}
            >
              {rentPreset !== "custom" ? (
                <input
                  type="text"
                  readOnly
                  placeholder="Select date"
                  value={eventDate ? formatDisplayDate(eventDate) : ""}
                  onFocus={() => setCalendarOpen(true)}
                  className="eventDateInput"
                />
              ) : null}
              {deliveryReturn && (() => {
                const daysUntilEvent = daysFromToday(eventDate);
                const deliveryKey = toDateKey(deliveryReturn.delivery);
                const daysUntilDelivery = daysFromToday(deliveryKey);
                const meetsBookInTime = daysUntilEvent !== null && daysUntilEvent >= MIN_DAYS_BEFORE_EVENT;
                const meetsDeliveryWindow = daysUntilDelivery !== null && daysUntilDelivery >= DELIVERY_DAYS_MIN;
                const allConditionsMet = meetsBookInTime && meetsDeliveryWindow;
                return (
                  <div className="productDeliveryReturnBlock">
                    <div className="productDeliveryReturnTimeline">
                      <div className="productDeliveryReturnItem">
                        <span className="productDeliveryReturnIcon event">
                          <HiOutlineCalendar size={20} />
                        </span>
                        <div className="productDeliveryReturnContent">
                          <span className="productDeliveryReturnLabel">Event</span>
                          <span className="productDeliveryReturnDate">{formatDisplayDate(deliveryReturn.event)}</span>
                        </div>
                      </div>
                      <span className="productDeliveryReturnArrow" aria-hidden="true">→</span>
                      <div className="productDeliveryReturnItem">
                        <span className="productDeliveryReturnIcon return">
                          <HiOutlineReply size={20} />
                        </span>
                        <div className="productDeliveryReturnContent">
                          <span className="productDeliveryReturnLabel">Return</span>
                          <span className="productDeliveryReturnDate">{formatDisplayDate(deliveryReturn.return)}</span>
                        </div>
                      </div>
                    </div>
                    <ul className={`productDeliveryReturnPolicyStatus ${allConditionsMet ? "met" : "warning"}`}>
                      {allConditionsMet ? (
                        <>
                          <li>✓ Estimate delivery 3–5 days met</li>
                          <li>✓ Booked 5+ days before event</li>
                          <li>Return by {formatDisplayDate(deliveryReturn.return)}</li>
                        </>
                      ) : (
                        <>
                          <li>Book at least 5 days before your event for reliable delivery</li>
                          <li>Estimate delivery: 3–5 days</li>
                          <li>Return within 3 days of last rental day</li>
                          <li>Duties & taxes are non-refundable</li>
                        </>
                      )}
                    </ul>
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="productDepositSection">
            <h4>Refundable Deposit</h4>
            <p>{DEPOSIT_NOTE}</p>
          </div>

          <button type="button" className="productWishlistBtn" onClick={toggleWishlist}>
            {isWishlisted ? "Saved to wishlist" : "Add to wishlist"}
          </button>

          <button type="button" className="productRentNowBtn" onClick={handleRentNow}>
            <RiShoppingCartLine size={22} />
            Rent Now
          </button>
        </div>
      </div>

      {termsModalOpen && (
        <div className="productTermsOverlay" onClick={() => setTermsModalOpen(false)}>
          <div className="productTermsModal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="productTermsModalClose" onClick={() => setTermsModalOpen(false)} aria-label="Close">
              ×
            </button>
            <h3 className="productTermsModalTitle">AGREEMENT TO OUR LEGAL TERMS</h3>
            <div className="productTermsModalContent">
              <p>
                We are <strong>Gem Fashion Studio</strong> (“Company,” “we,” “us,” “our”), a company registered in India at SMRUDHI VENTURE PARK UNIT 2-7 LOWER GROUND FLOOR MAROL INDUSTRIAL AREA ANDHERI EAST, MIDC Maharashtra, Mumbai, Maharastra 400093.
              </p>
              <p>
                We operate the website gemfashionstudio.in (the “Site”), as well as any other related products and services that refer or link to these legal terms (the “Legal Terms”) (collectively, the “Services”).
              </p>
              <p>
                You can contact us by phone at +91 98700 26839, email at gemboutiqueinquiries@gmail.com, or by mail to SMRUDHI VENTURE PARK UNIT 2-7 LOWER GROUND FLOOR MAROL INDUSTRIAL AREA ANDHERI EAST, MIDC Maharashtra, Mumbai, Maharastra 400093, India.
              </p>
              <p className="productTermsModalMore">
                For more details refer to <Link to="/terms" onClick={() => setTermsModalOpen(false)}>Terms & Conditions</Link>.
              </p>
            </div>
          </div>
        </div>
      )}

      {depositModalOpen && (
        <div className="productTermsOverlay" onClick={() => setDepositModalOpen(false)}>
          <div className="productTermsModal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="productTermsModalClose" onClick={() => setDepositModalOpen(false)} aria-label="Close">
              ×
            </button>
            <h3 className="productTermsModalTitle">Returns + Exchanges</h3>
            <div className="productTermsModalContent">
              <p>
                At Gem Fashion Studio we strive to provide you with high-quality clothing for your special occasions. To ensure the upkeep of our inventory and maintain the quality of our outfits, we have established the following deposit and refund policy:
              </p>
              <p><strong>Deposit:</strong></p>
              <p>
                A deposit will be required upon booking your rental order. This deposit will be refunded to you after the return of the rented outfit, subject to the conditions outlined below.
              </p>
              <p><strong>Refund Deductions:</strong></p>
              <ul>
                <li><strong>0% to 30% Deduction:</strong> This applies if the outfit has minor issues such as stitching work, small holes, removable stains, or minor alterations like button, latkan, Dori, or zip changes.</li>
                <li><strong>31% to 70% Deduction:</strong> If there is a need for significant alterations or repairs, such as replacing a lace, part of fabric, or entire fabric rework/change due to substantial damage like complete tearing or stretching out.</li>
                <li><strong>71% to 100% Deduction:</strong> This applies if the outfit is severely damaged beyond repair or if it cannot be fixed at all.</li>
              </ul>
              <p>
                <em>Note: The specific deduction percentage within each range will be determined based on the extent of the damage or alteration required.</em>
              </p>
              <p><strong>Return Procedure:</strong></p>
              <ul>
                <li>Upon returning the rented outfit, it will be inspected for any damages or alterations.</li>
                <li>If any deductions apply, the refund amount will be adjusted accordingly.</li>
                <li>The remaining deposit amount will be refunded to the original payment method within 4 to 5 Business Days after the inspection.</li>
              </ul>
              <p><strong>Customer Responsibilities:</strong></p>
              <ul>
                <li>Customers are responsible for handling the rented outfits with care and following any care instructions provided.</li>
                <li>Any damages beyond normal wear and tear will result in deductions from the deposit.</li>
              </ul>
              <p><strong>Questions or Concerns:</strong> If you have any questions or concerns regarding our deposit and refund policy, please feel free to contact us at <a href="mailto:gemboutiqueinquiries@gmail.com">gemboutiqueinquiries@gmail.com</a>.</p>
              <p>
                By booking a rental order with us, you agree to abide by the terms outlined in this deposit and refund policy.
              </p>
            </div>
          </div>
        </div>
      )}

      {deliveryModalOpen && (
        <div className="productTermsOverlay" onClick={() => setDeliveryModalOpen(false)}>
          <div className="productTermsModal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="productTermsModalClose" onClick={() => setDeliveryModalOpen(false)} aria-label="Close">
              ×
            </button>
            <h3 className="productTermsModalTitle">Free pick up / Delivery</h3>
            <div className="productTermsModalContent">
              <p><strong>Estimate delivery times: 3–5 Days</strong></p>
              <p>Please ensure to book the order 5 to 7 days before the event.</p>
              <p>Return within 3 days of purchase. Duties & taxes are non-refundable.</p>
            </div>
          </div>
        </div>
      )}

      {sizeChartModalOpen && (
        <div className="productTermsOverlay" onClick={() => setSizeChartModalOpen(false)}>
          <div className="productTermsModal productSizeChartModal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="productTermsModalClose" onClick={() => setSizeChartModalOpen(false)} aria-label="Close">
              ×
            </button>
            <h3 className="productTermsModalTitle">Size Chart (India)</h3>
            <div className="productTermsModalContent">
              <p className="productSizeChartIntro">
                Reference guide for standard Indian clothing sizes. Sizes may vary slightly by brand. Measure over lightweight clothing.
              </p>

              <h4 className="productSizeChartSubtitle">Women&apos;s (Bust – Waist – Hip in cm)</h4>
              <div className="productSizeChartTableWrap">
                <table className="productSizeChartTable">
                  <thead>
                    <tr>
                      <th>Size</th>
                      <th>Bust (cm)</th>
                      <th>Waist (cm)</th>
                      <th>Hip (cm)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>XS</td><td>81</td><td>66</td><td>89</td></tr>
                    <tr><td>S</td><td>86</td><td>71</td><td>94</td></tr>
                    <tr><td>M</td><td>91.5</td><td>76</td><td>99</td></tr>
                    <tr><td>L</td><td>97</td><td>81</td><td>104</td></tr>
                    <tr><td>XL</td><td>102</td><td>86</td><td>109</td></tr>
                    <tr><td>XXL</td><td>107</td><td>91</td><td>114</td></tr>
                  </tbody>
                </table>
              </div>

              <h4 className="productSizeChartSubtitle">Men&apos;s (Chest – Waist – Hip in cm)</h4>
              <div className="productSizeChartTableWrap">
                <table className="productSizeChartTable">
                  <thead>
                    <tr>
                      <th>Size</th>
                      <th>Chest (cm)</th>
                      <th>Waist (cm)</th>
                      <th>Hip (cm)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>S</td><td>86–90</td><td>71–76</td><td>86–91</td></tr>
                    <tr><td>M</td><td>91–96</td><td>76–81</td><td>91–96</td></tr>
                    <tr><td>L</td><td>97–102</td><td>81–86</td><td>96–101</td></tr>
                    <tr><td>XL</td><td>102–107</td><td>86–91</td><td>101–106</td></tr>
                    <tr><td>XXL</td><td>107–112</td><td>91–96</td><td>106–111</td></tr>
                  </tbody>
                </table>
              </div>

              <p className="productSizeChartNote">
                <strong>How to measure:</strong> Bust/Chest — fullest part; Waist — narrowest part (above navel); Hip — widest part. Measure snug but not tight. For a perfect fit, refer to individual product descriptions or contact us at <a href="mailto:gemboutiqueinquiries@gmail.com">gemboutiqueinquiries@gmail.com</a>.
              </p>
            </div>
          </div>
        </div>
      )}

      {calendarOpen && (
        <div className="productTermsOverlay" onClick={() => setCalendarOpen(false)}>
          <div className="productCalendarModal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="productTermsModalClose" onClick={() => setCalendarOpen(false)} aria-label="Close">
              ×
            </button>
            <h3 className="productTermsModalTitle">Select Event Date</h3>
            <p className="productCalendarHint">
              Rent period: {rentDays === 1 ? "1 day" : `${rentDays} days`}
              {rentPreset === "custom" && (!customRangeStart || !customRangeEnd)
                ? " (preview until you set custom rental dates)"
                : ""}
              . Past dates and unavailable dates are marked with X.
            </p>
            <div className="productCalendarHeader">
              <button type="button" className="productCalendarNav" onClick={calendarPrevYear} aria-label="Previous year">&#x00AB;</button>
              <button type="button" className="productCalendarNav" onClick={calendarPrevMonth} aria-label="Previous month">&#x2039;</button>
              <span className="productCalendarMonthYear">{MONTHS[calendarMonth.month]} – {calendarMonth.year}</span>
              <button type="button" className="productCalendarNav" onClick={calendarNextMonth} aria-label="Next month">&#x203A;</button>
              <button type="button" className="productCalendarNav" onClick={calendarNextYear} aria-label="Next year">&#x00BB;</button>
            </div>
            <div className="productCalendarWeekdays">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <span key={day} className="productCalendarWeekday">{day}</span>
              ))}
            </div>
            <div className="productCalendarGrid">
              {calendarGrid.map((cell, i) => (
                <div
                  key={cell.dateKey ? cell.dateKey : `empty-${i}`}
                  role="button"
                  tabIndex={cell.isCurrentMonth && !cell.isUnavailable ? 0 : -1}
                  className={`productCalendarCell ${!cell.isCurrentMonth ? "otherMonth" : ""} ${cell.isUnavailable ? "unavailable" : ""} ${cell.isInRange ? "inRange" : ""} ${cell.isEventDate ? "eventDate" : ""} ${cell.isCurrentMonth && !cell.isUnavailable ? "selectable" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (cell.isCurrentMonth && !cell.isUnavailable && cell.dateKey) {
                      handleCalendarDateSelect(cell.dateKey);
                    }
                  }}
                >
                  {cell.isUnavailable ? "X" : cell.dateKey ? new Date(cell.dateKey).getDate() : ""}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {rentalRangeModalOpen && (
        <div className="productTermsOverlay" onClick={closeRentalRangeModal}>
          <div className="productCalendarModal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="productTermsModalClose" onClick={closeRentalRangeModal} aria-label="Close">
              ×
            </button>
            <h3 className="productTermsModalTitle">Rental period (inclusive)</h3>
            <p className="productCalendarHint">
              {rentRangePickStart
                ? "Tap the end date (we fill the range in between)."
                : "Tap your start date, then your end date on the same calendar."}{" "}
              Past dates and unavailable dates are marked with X.
            </p>
            <div className="productCalendarHeader">
              <button type="button" className="productCalendarNav" onClick={rentalRangePrevYear} aria-label="Previous year">&#x00AB;</button>
              <button type="button" className="productCalendarNav" onClick={rentalRangePrevMonth} aria-label="Previous month">&#x2039;</button>
              <span className="productCalendarMonthYear">{MONTHS[rentalRangeMonth.month]} – {rentalRangeMonth.year}</span>
              <button type="button" className="productCalendarNav" onClick={rentalRangeNextMonth} aria-label="Next month">&#x203A;</button>
              <button type="button" className="productCalendarNav" onClick={rentalRangeNextYear} aria-label="Next year">&#x00BB;</button>
            </div>
            <div className="productCalendarWeekdays">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <span key={day} className="productCalendarWeekday">{day}</span>
              ))}
            </div>
            <div className="productCalendarGrid">
              {rentalRangeGrid.map((cell, i) => (
                <div
                  key={cell.dateKey ? cell.dateKey : `rent-empty-${i}`}
                  role="button"
                  tabIndex={cell.isCurrentMonth && !cell.isUnavailable ? 0 : -1}
                  className={[
                    "productCalendarCell",
                    !cell.isCurrentMonth ? "otherMonth" : "",
                    cell.isUnavailable ? "unavailable" : "",
                    cell.isRentalInRange ? "inRange" : "",
                    cell.isRentalSingle ? "rentalRangeSingle" : "",
                    cell.isRentalStart ? "rentalRangeStart" : "",
                    cell.isRentalEnd ? "rentalRangeEnd" : "",
                    cell.isRentalMid ? "rentalRangeMid" : "",
                    cell.isCurrentMonth && !cell.isUnavailable ? "selectable" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (cell.isCurrentMonth && !cell.isUnavailable && cell.dateKey) {
                      handleRentalRangeCellClick(cell.dateKey);
                    }
                  }}
                >
                  {cell.isUnavailable ? "X" : cell.dateKey ? new Date(`${cell.dateKey}T12:00:00`).getDate() : ""}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tryOnModalOpen && (
        <Suspense fallback={<div className="productTermsOverlay" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>Loading…</div>}>
          <TryOnModal
            open={tryOnModalOpen}
            onClose={() => setTryOnModalOpen(false)}
            dressImageUrl={productImages[0]}
            productName={productName}
            onContinue={() => {
              setTryOnModalOpen(false);
              document.querySelector(".productRentNowBtn")?.scrollIntoView({ behavior: "smooth" });
            }}
          />
        </Suspense>
      )}

      <AskAISuggestionsModal
        open={askAiModalOpen}
        onClose={() => setAskAiModalOpen(false)}
        stylingSnippets={product?.styling_snippets || null}
      />

      <section className="productReviewsSection" aria-labelledby="product-reviews-title">
        <div className="productReviewsSummary">
          <h2 id="product-reviews-title">Customer reviews</h2>
          {reviewSummary.count > 0 ? (
            <p><strong>{reviewSummary.average} / 5</strong> from {reviewSummary.count} approved review{reviewSummary.count === 1 ? "" : "s"}</p>
          ) : (
            <p>No approved customer reviews yet.</p>
          )}
        </div>
        {reviews.length > 0 && (
          <div className="productReviewsList">
            {reviews.map((r) => (
              <article key={r.id} className="productReviewCard">
                <strong>{"★".repeat(Number(r.rating))}{"☆".repeat(5 - Number(r.rating))}</strong>
                {r.title ? <h3>{r.title}</h3> : null}
                <p>{r.comment}</p>
                <small>{r.display_name}{r.verified_rental ? " · Verified rental" : ""}</small>
              </article>
            ))}
          </div>
        )}
        <form className="productReviewForm" onSubmit={submitReview}>
          <h3>Write a review</h3>
          <label>Rating
            <select value={reviewForm.rating} onChange={(e) => setReviewForm((p) => ({ ...p, rating: Number(e.target.value) }))}>
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <input value={reviewForm.title} onChange={(e) => setReviewForm((p) => ({ ...p, title: e.target.value }))} placeholder="Review title" />
          <textarea required minLength={10} value={reviewForm.comment} onChange={(e) => setReviewForm((p) => ({ ...p, comment: e.target.value }))} placeholder="Tell us about your rental experience" />
          <button type="submit" disabled={reviewSubmitting}>{reviewSubmitting ? "Submitting…" : "Submit for moderation"}</button>
        </form>
      </section>

      {recentlyViewed.length > 0 && (
        <section className="recentlyViewedSection" aria-label="Recently viewed products">
          <h2>Recently viewed</h2>
          <div className="recentlyViewedGrid">
            {recentlyViewed.map((item) => (
              <Link key={String(item.id)} to={`/product/${item.id}`} className="recentlyViewedCard">
                {item.frontImg || item.image ? <img src={resolveImageSrc(item.frontImg || item.image)} alt={item.productName || item.name} /> : null}
                <span>{item.productName || item.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <RelatedProducts categoryId={product.category_id} excludeProductId={product.id} />
    </div>
    </>
  );
};

export default Product;
